import React, { useState, useEffect, useContext, useCallback } from "react";
import { toast } from "react-toastify";
import { useHistory } from "react-router-dom";
import moment from "moment";

import { makeStyles, useTheme } from "@material-ui/core/styles";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import {
  Button,
  Chip,
  IconButton,
  InputAdornment,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@material-ui/core";
import SearchIcon from "@material-ui/icons/Search";
import CloseIcon from "@material-ui/icons/Close";
import EditIcon from "@material-ui/icons/Edit";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import AnnouncementOutlinedIcon from "@material-ui/icons/AnnouncementOutlined";

import TableRowSkeleton from "../../components/TableRowSkeleton";
import MobileRecordCard, { MobileRecordCardSkeleton } from "../../components/MobileRecordCard";
import AnnouncementModal from "../../components/AnnouncementModal";
import ConfirmationModal from "../../components/ConfirmationModal";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { socketConnection } from "../../services/socket";
import { AuthContext } from "../../context/Auth/AuthContext";

// Mesmas cores da borda do sino do cabeçalho (AnnouncementsPopover).
const PRIORITIES = {
  1: { label: "Alta", color: "#d93025" },
  2: { label: "Média", color: "#f29900" },
  3: { label: "Baixa", color: "#80868b" },
};

const STATUS_FILTERS = [
  { value: "all", label: "Todos" },
  { value: "active", label: "Ativos" },
  { value: "inactive", label: "Inativos" },
];

const mediaUrl = (filename) => `${process.env.REACT_APP_BACKEND_URL}/public/${filename}`;

// "Hoje, 14:32" / "Ontem, 09:10" / "03/10/2026"
const formatDate = (date) => {
  const m = moment(date);
  if (m.isSame(moment(), "day")) return `Hoje, ${m.format("HH:mm")}`;
  if (m.isSame(moment().subtract(1, "day"), "day")) return `Ontem, ${m.format("HH:mm")}`;
  return m.format("DD/MM/YYYY");
};

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
    maxWidth: 1100,
    margin: "0 auto",
    width: "100%",
    overflowY: "auto",
    ...theme.scrollbarStyles,
    [theme.breakpoints.down("xs")]: { padding: theme.spacing(2) },
  },
  header: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
  title: { fontWeight: 700, color: theme.palette.text.primary },
  summary: { fontSize: "0.875rem", color: theme.palette.text.secondary },
  toolbar: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    gap: theme.spacing(1.5),
    marginBottom: theme.spacing(1.5),
  },
  search: {
    flex: "1 1 260px",
    maxWidth: 420,
    [theme.breakpoints.down("xs")]: { maxWidth: "none" },
  },
  tabs: { display: "flex", gap: theme.spacing(1), flexWrap: "wrap" },
  tablePaper: { borderRadius: theme.spacing(1), overflow: "hidden" },
  cellTitle: {
    fontWeight: 600,
    maxWidth: 420,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  cellPreview: {
    fontSize: "0.8rem",
    color: theme.palette.text.secondary,
    maxWidth: 420,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  priorityChip: { height: 22, fontSize: "0.75rem", fontWeight: 600, color: "#fff" },
  thumb: {
    width: 44,
    height: 44,
    borderRadius: theme.spacing(1),
    objectFit: "cover",
    display: "block",
    border: `1px solid ${theme.palette.divider}`,
  },
  thumbEmpty: {
    width: 44,
    height: 44,
    borderRadius: theme.spacing(1),
    border: `1px dashed ${theme.palette.divider}`,
  },
  emptyState: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    padding: theme.spacing(8, 2),
    color: theme.palette.text.secondary,
    gap: theme.spacing(1),
    textAlign: "center",
  },
  emptyIcon: { fontSize: 56, opacity: 0.35 },
  loadMore: { display: "flex", justifyContent: "center", marginTop: theme.spacing(2) },
}));

const PriorityChip = ({ value, className }) => {
  const p = PRIORITIES[value] || PRIORITIES[3];
  return <Chip size="small" label={p.label} className={className} style={{ backgroundColor: p.color }} />;
};

const Announcements = () => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const history = useHistory();
  const { user } = useContext(AuthContext);

  const [records, setRecords] = useState([]);
  const [counts, setCounts] = useState({ active: 0, inactive: 0 });
  const [hasMore, setHasMore] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [statusFilter, setStatusFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [deleting, setDeleting] = useState(null);

  const total = counts.active + counts.inactive;

  useEffect(() => {
    if (!user.super) {
      toast.error("Esta página é restrita ao administrador do sistema.");
      history.push("/");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 400);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    setRecords([]);
    setPageNumber(1);
  }, [statusFilter, debouncedSearch]);

  useEffect(() => {
    if (!user.super) return;
    let cancelled = false;

    const fetchAnnouncements = async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/announcements", {
          params: {
            pageNumber,
            status: statusFilter,
            searchParam: debouncedSearch || undefined,
          },
        });
        if (cancelled) return;
        setRecords((prev) => (pageNumber === 1 ? data.records : [...prev, ...data.records]));
        setCounts(data.counts || { active: 0, inactive: 0 });
        setHasMore(data.hasMore);
      } catch (err) {
        if (!cancelled) toastError(err);
      }
      if (!cancelled) setLoading(false);
    };

    fetchAnnouncements();
    return () => {
      cancelled = true;
    };
  }, [pageNumber, statusFilter, debouncedSearch, reloadKey, user.super]);

  // Outra aba/usuário super mexeu: recarrega a lista (e os contadores).
  useEffect(() => {
    if (!user.super) return undefined;
    const socket = socketConnection({ companyId: user.companyId, userId: user.id });
    socket.on("company-announcement", () => {
      setPageNumber(1);
      setReloadKey((k) => k + 1);
    });
    return () => socket.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const reload = useCallback(() => {
    setPageNumber(1);
    setReloadKey((k) => k + 1);
  }, []);

  const handleOpenModal = (announcement) => {
    setEditingId(announcement ? announcement.id : null);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setEditingId(null);
    setModalOpen(false);
  };

  const handleToggleStatus = async (announcement) => {
    const status = !announcement.status;
    try {
      await api.put(`/announcements/${announcement.id}`, {
        title: announcement.title,
        text: announcement.text,
        priority: announcement.priority,
        status,
      });
      toast.success(status ? "Informativo ativado." : "Informativo desativado.");
      reload();
    } catch (err) {
      toastError(err);
    }
  };

  const handleDelete = async () => {
    const announcement = deleting;
    setDeleting(null);
    try {
      await api.delete(`/announcements/${announcement.id}`);
      toast.success("Informativo excluído.");
      reload();
    } catch (err) {
      toastError(err);
    }
  };

  const renderEmpty = () => {
    let msg = "Nenhum informativo criado ainda.";
    if (debouncedSearch) msg = `Nenhum informativo encontrado para "${debouncedSearch}".`;
    else if (statusFilter === "active") msg = "Nenhum informativo ativo no momento.";
    else if (statusFilter === "inactive") msg = "Nenhum informativo inativo.";
    return (
      <div className={classes.emptyState}>
        <AnnouncementOutlinedIcon className={classes.emptyIcon} />
        <Typography>{msg}</Typography>
      </div>
    );
  };

  const renderActions = (a) => (
    <>
      <Tooltip title="Editar">
        <IconButton size="small" onClick={() => handleOpenModal(a)}>
          <EditIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title="Excluir">
        <IconButton size="small" onClick={() => setDeleting(a)}>
          <DeleteOutlineIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </>
  );

  const renderCards = () => (
    <div>
      {records.map((a) => (
        <MobileRecordCard
          key={a.id}
          title={a.title}
          subtitle={formatDate(a.createdAt)}
          headerAction={<PriorityChip value={a.priority} className={classes.priorityChip} />}
          body={
            <Typography variant="body2" color="textSecondary">
              {a.text.length > 140 ? `${a.text.slice(0, 140)}…` : a.text}
            </Typography>
          }
          rows={[
            {
              label: "Ativo",
              value: <Switch size="small" color="primary" checked={!!a.status} onChange={() => handleToggleStatus(a)} />,
            },
            ...(a.mediaName ? [{ label: "Imagem", value: a.mediaName }] : []),
          ]}
          footer={
            <div style={{ display: "flex", gap: 8 }}>
              <Button size="small" variant="outlined" fullWidth startIcon={<EditIcon />} onClick={() => handleOpenModal(a)}>
                Editar
              </Button>
              <Button size="small" variant="outlined" fullWidth startIcon={<DeleteOutlineIcon />} onClick={() => setDeleting(a)}>
                Excluir
              </Button>
            </div>
          }
        />
      ))}
      {loading && [0, 1, 2].map((i) => <MobileRecordCardSkeleton key={i} />)}
    </div>
  );

  const renderTable = () => (
    <Paper className={classes.tablePaper} variant="outlined">
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Prioridade</TableCell>
            <TableCell>Informativo</TableCell>
            <TableCell>Imagem</TableCell>
            <TableCell>Publicado em</TableCell>
            <TableCell align="center">Ativo</TableCell>
            <TableCell align="right" />
          </TableRow>
        </TableHead>
        <TableBody>
          {records.map((a) => (
            <TableRow key={a.id} hover>
              <TableCell>
                <PriorityChip value={a.priority} className={classes.priorityChip} />
              </TableCell>
              <TableCell>
                <div className={classes.cellTitle} title={a.title}>{a.title}</div>
                <div className={classes.cellPreview}>{a.text}</div>
              </TableCell>
              <TableCell>
                {a.mediaPath ? (
                  <img src={mediaUrl(a.mediaPath)} alt={a.mediaName || a.title} className={classes.thumb} />
                ) : (
                  <div className={classes.thumbEmpty} />
                )}
              </TableCell>
              <TableCell>{formatDate(a.createdAt)}</TableCell>
              <TableCell align="center">
                <Tooltip title={a.status ? "Visível para todas as empresas" : "Oculto"}>
                  <Switch size="small" color="primary" checked={!!a.status} onChange={() => handleToggleStatus(a)} />
                </Tooltip>
              </TableCell>
              <TableCell align="right">{renderActions(a)}</TableCell>
            </TableRow>
          ))}
          {loading && <TableRowSkeleton columns={6} />}
        </TableBody>
      </Table>
    </Paper>
  );

  return (
    <div className={classes.root}>
      <ConfirmationModal
        title={deleting ? `Excluir o informativo "${deleting.title}"?` : ""}
        open={Boolean(deleting)}
        onClose={() => setDeleting(null)}
        onConfirm={handleDelete}
      >
        Ele deixará de aparecer no sino de todas as empresas. Esta ação não pode ser desfeita.
      </ConfirmationModal>

      <AnnouncementModal
        open={modalOpen}
        onClose={handleCloseModal}
        reload={reload}
        announcementId={editingId}
      />

      <div className={classes.header}>
        <div>
          <Typography variant="h5" className={classes.title}>
            Informativos
          </Typography>
          <Typography className={classes.summary}>
            {counts.active} ativo{counts.active !== 1 ? "s" : ""} · {counts.inactive} inativo
            {counts.inactive !== 1 ? "s" : ""} — aparecem no sino do cabeçalho de <b>todas</b> as empresas
          </Typography>
        </div>
        <Button variant="contained" color="primary" onClick={() => handleOpenModal(null)}>
          + Novo informativo
        </Button>
      </div>

      <div className={classes.toolbar}>
        <TextField
          className={classes.search}
          placeholder="Buscar no título ou no texto"
          variant="outlined"
          size="small"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
            endAdornment: search ? (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setSearch("")}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
        />
        <div className={classes.tabs}>
          {STATUS_FILTERS.map((s) => {
            const n = s.value === "all" ? total : counts[s.value] || 0;
            const active = statusFilter === s.value;
            return (
              <Chip
                key={s.value}
                label={`${s.label} (${n})`}
                onClick={() => setStatusFilter(s.value)}
                color={active ? "primary" : "default"}
                variant={active ? "default" : "outlined"}
                size="small"
              />
            );
          })}
        </div>
      </div>

      {!loading && records.length === 0 ? renderEmpty() : isMobile ? renderCards() : renderTable()}

      {hasMore && !loading && (
        <div className={classes.loadMore}>
          <Button size="small" onClick={() => setPageNumber((prev) => prev + 1)}>
            Carregar mais
          </Button>
        </div>
      )}
    </div>
  );
};

export default Announcements;
